import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { GraphicEq } from '@mui/icons-material';
import { GREENING_BRAND } from '../greeningFabActions';
import {
  displayStartVoiceProfileId,
  getPreferredVoiceProfileId,
  listVoiceProfiles,
  setPreferredVoiceProfileId,
} from './voiceProfiles';

const TEXT_PLATE = `linear-gradient(135deg, ${GREENING_BRAND.green} 0%, ${GREENING_BRAND.teal} 100%)`;

interface Props {
  onChange?: (profileId: string) => void;
  compact?: boolean;
  /** Ids de perfil que não aparecem neste seletor. A preferência salva não muda. */
  excludeIds?: string[];
  /** Rótulos e círculos em cor cheia, para o painel vazado. */
  vivid?: boolean;
}

/** Avatares clicáveis: clique = voz preferida (local). ATIVO = destaque visual. */
export function GreeningVoicePicker({ onChange, compact = false, excludeIds = [], vivid = false }: Props) {
  const [profileId, setProfileId] = useState(() => {
    const stored = getPreferredVoiceProfileId();
    if (excludeIds.includes(stored)) return displayStartVoiceProfileId();
    return stored;
  });
  const size = compact ? 44 : 52;

  return (
    <Box
      role="radiogroup"
      aria-label="Escolher voz da GreeningAI"
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        gap: compact ? 1.5 : 2,
        mb: 0.5,
      }}
    >
      {listVoiceProfiles().filter((profile) => !excludeIds.includes(profile.id)).map((profile) => {
        const selected = profile.id === profileId;
        return (
          <Box
            key={profile.id}
            component="button"
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={profile.displayName}
            onClick={() => {
              setPreferredVoiceProfileId(profile.id);
              setProfileId(profile.id);
              onChange?.(profile.id);
            }}
            sx={{
              appearance: 'none',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              p: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.4,
              opacity: vivid || selected ? 1 : 0.55,
              transition: 'transform 160ms ease, opacity 160ms ease',
              transform: selected ? 'scale(1.08)' : 'scale(1)',
              '&:hover': { opacity: 1, transform: 'scale(1.08)' },
              '&:focus-visible': {
                outline: `2px solid ${profile.accent}`,
                outlineOffset: 3,
                borderRadius: 2,
              },
            }}
          >
            <Box
              sx={{
                width: size,
                height: size,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                bgcolor: vivid
                  ? (selected ? profile.accent : alpha(profile.accent, 0.72))
                  : alpha(profile.accent, selected ? 0.18 : 0.08),
                border: selected ? `3px solid ${profile.accent}` : `1.5px solid ${alpha(profile.accent, vivid ? 0.9 : 0.35)}`,
                boxShadow: selected ? `0 0 0 4px ${alpha(profile.accent, 0.28)}` : 'none',
              }}
            >
              <GraphicEq sx={{ fontSize: compact ? 22 : 26, color: vivid ? '#fff' : profile.accent }} />
            </Box>
            <Box
              sx={
                vivid
                  ? {
                      px: 0.85,
                      py: 0.35,
                      borderRadius: '12px',
                      backgroundImage: TEXT_PLATE,
                      textAlign: 'center',
                    }
                  : { textAlign: 'center' }
              }
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  color: vivid ? '#fff' : selected ? profile.accent : 'text.secondary',
                  lineHeight: 1.1,
                }}
              >
                {profile.displayName}
              </Typography>
              {selected ? (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    fontSize: 10,
                    fontWeight: 700,
                    color: vivid ? '#fff' : profile.accent,
                    letterSpacing: 0.6,
                    lineHeight: 1,
                    mt: 0.25,
                  }}
                >
                  ATIVO
                </Typography>
              ) : (
                <Box sx={{ height: 10 }} />
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
